'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface WaitlistFormClientProps {
  schoolSlug: string
  eventPublicId: string
}

export function WaitlistFormClient({ schoolSlug, eventPublicId }: WaitlistFormClientProps) {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [desiredQuantity, setDesiredQuantity] = useState(1)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  if (submitted) {
    return (
      <div className="rounded-lg border bg-muted p-6 text-center flex flex-col gap-2">
        <h2 className="text-lg font-semibold">You&apos;re on the waitlist</h2>
        <p className="text-sm text-muted-foreground">
          We&apos;ll email you if a spot opens up. Keep an eye on your inbox.
        </p>
      </div>
    )
  }

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={async e => {
        e.preventDefault()
        setError(null)
        if (!email || !firstName || !lastName) {
          setError('Please fill in all required fields.')
          return
        }
        if (desiredQuantity < 1) {
          setError('Quantity must be at least 1.')
          return
        }
        setSubmitting(true)
        try {
          const base = process.env.NEXT_PUBLIC_BACKEND_URL
          const res = await fetch(
            `${base}/v1/ticketed-events/by-slug/${encodeURIComponent(schoolSlug)}/${encodeURIComponent(eventPublicId)}/waitlist`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                email,
                firstName,
                lastName,
                phone: phone || undefined,
                desiredQuantity
              })
            }
          )
          if (!res.ok) {
            const json = (await res.json().catch(() => ({}))) as { error?: string }
            setError(json.error ?? 'Could not join the waitlist. Please try again.')
            return
          }
          setSubmitted(true)
        } finally {
          setSubmitting(false)
        }
      }}
    >
      <div className="rounded-lg border bg-muted p-4">
        <h2 className="text-lg font-semibold">This event is full</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Join the waitlist and we&apos;ll email you if a spot opens up.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor="wl-firstName">First name</Label>
          <Input id="wl-firstName" value={firstName} onChange={e => setFirstName(e.target.value)} required />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="wl-lastName">Last name</Label>
          <Input id="wl-lastName" value={lastName} onChange={e => setLastName(e.target.value)} required />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="wl-email">Email</Label>
        <Input id="wl-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="wl-phone">Phone (optional)</Label>
        <Input id="wl-phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)} />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="wl-qty">Spots needed</Label>
        <Input
          id="wl-qty"
          type="number"
          min={1}
          value={desiredQuantity}
          onChange={e => setDesiredQuantity(Number(e.target.value))}
        />
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <Button type="submit" disabled={submitting} size="lg">
        {submitting ? 'Joining…' : 'Join waitlist'}
      </Button>
    </form>
  )
}
